// Wave 20.21 — Live-friends section for the Feed page.
//
// Fetches GET /feed/live (users I follow who are currently in a
// session). Each card shows:
//   - Actor + activity type + running duration
//   - Live pulse indicator
//   - Telemetry the actor has opted to share (power / speed / HR
//     / cadence / elapsed). No opt-in? Just the "live" chip.
//   - Small "location on" hint when they've opted into location
//     sharing (Push C — no live map yet; that ships later).
//
// Polls every 20s so a viewer who leaves the tab open sees people
// come and go.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Stack,
  Typography,
  keyframes,
} from '@mui/material';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { Link as RouterLink } from 'react-router-dom';
import { colors } from '../theme';
import { fmtDistance, fmtSpeed, useUnitSystem } from '../services/useUnitSystem';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

interface LiveRow {
  userId: number;
  actor: { id: number; username: string | null; displayName: string };
  activityType: 'indoor_aero' | 'outdoor_cycling' | 'outdoor_running';
  startedAt: string;
  lastHeartbeatAt: string;
  shareStream: boolean;
  shareLocation: boolean;
  snapshot: Record<string, any>;
}

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.35; }
`;

export default function LiveFriends() {
  const [rows, setRows] = useState<LiveRow[] | null>(null);

  const load = useCallback(async () => {
    const jwt = localStorage.getItem(TOKEN_KEY);
    if (!jwt) return;
    try {
      const res = await fetch(`${API}/feed/live`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) return;
      setRows(await res.json());
    } catch {
      // Best-effort — feed still renders without live cards.
    }
  }, []);

  useEffect(() => {
    load();
    // Refresh every 20s so a live rider dropping off in the last
    // 90s (heartbeat window) disappears from the list within a
    // beat, and someone starting mid-view shows up promptly.
    const t = setInterval(load, 20_000);
    return () => clearInterval(t);
  }, [load]);

  if (!rows || rows.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: colors.brand,
            animation: `${pulse} 1.6s ease-in-out infinite`,
          }}
        />
        <Typography
          variant="overline"
          sx={{ color: colors.brand, letterSpacing: '0.2em', fontWeight: 800 }}
        >
          Live now · {rows.length}
        </Typography>
      </Stack>
      <Stack spacing={1}>
        {rows.map((r) => (
          <LiveCard key={r.userId} row={r} />
        ))}
      </Stack>
    </Box>
  );
}

function LiveCard({ row }: { row: LiveRow }) {
  const units = useUnitSystem();
  const Icon =
    row.activityType === 'outdoor_running'
      ? DirectionsRunIcon
      : row.activityType === 'outdoor_cycling'
      ? DirectionsBikeIcon
      : CameraAltIcon;
  const label =
    row.activityType === 'indoor_aero'
      ? 'Aero session'
      : row.activityType === 'outdoor_running'
      ? 'Run'
      : 'Ride';
  const startedMs = new Date(row.startedAt).getTime();
  const elapsedSec = Math.max(0, Math.floor((Date.now() - startedMs) / 1000));
  const m = Math.floor(elapsedSec / 60);
  const s = elapsedSec % 60;
  const durLabel = `${m}:${String(s).padStart(2, '0')}`;
  const hasStream = row.shareStream;
  const link = row.actor.username ? `/u/${row.actor.username}` : undefined;

  return (
    <Box
      component={link ? RouterLink : 'div'}
      to={link}
      sx={{
        display: 'flex',
        gap: 2,
        p: 1.5,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.brand}55`,
        boxShadow: `0 0 0 1px ${colors.brand}18, 0 12px 28px ${colors.brand}0f`,
        textDecoration: 'none',
        color: 'inherit',
        cursor: link ? 'pointer' : 'default',
        transition: 'border-color 120ms',
        '&:hover': link ? { borderColor: colors.brand } : {},
      }}
    >
      <Box sx={{ pt: 0.5 }}>
        <Icon sx={{ color: colors.brand, fontSize: 28 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="baseline"
          sx={{ flexWrap: 'wrap' }}
        >
          <Typography sx={{ fontWeight: 800, color: colors.text }}>
            {row.actor.displayName}
          </Typography>
          {row.actor.username && (
            <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>
              @{row.actor.username}
            </Typography>
          )}
          <Chip
            label={`● LIVE · ${durLabel}`}
            size="small"
            sx={{
              backgroundColor: colors.brand,
              color: colors.textInverse,
              fontWeight: 800,
              letterSpacing: '0.08em',
              fontSize: '0.65rem',
              height: 20,
            }}
          />
          {/* Wave 20.23 — live location retired; map chip removed. */}
        </Stack>
        <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mt: 0.25 }}>
          {label}
        </Typography>

        {hasStream ? (
          <Stack direction="row" spacing={3} sx={{ mt: 1, flexWrap: 'wrap', gap: 1.5 }}>
            {row.snapshot.power_w != null && (
              <Metric label="Power" value={`${Math.round(row.snapshot.power_w)} W`} />
            )}
            {row.snapshot.speed_mps != null && (
              <Metric label="Speed" value={fmtSpeed(row.snapshot.speed_mps, units)} />
            )}
            {row.snapshot.hr_bpm != null && (
              <Metric label="HR" value={`${Math.round(row.snapshot.hr_bpm)} bpm`} />
            )}
            {row.snapshot.cadence_rpm != null && (
              <Metric label="Cadence" value={`${Math.round(row.snapshot.cadence_rpm)}`} />
            )}
            {row.snapshot.distance_m != null && row.snapshot.distance_m > 0 && (
              <Metric
                label="Distance"
                value={fmtDistance(row.snapshot.distance_m, units)}
              />
            )}
            {row.snapshot.current_cda != null && (
              <Metric label="CdA" value={row.snapshot.current_cda.toFixed(3)} />
            )}
            {row.snapshot.current_position && (
              <Metric label="Position" value={String(row.snapshot.current_position)} />
            )}
          </Stack>
        ) : (
          <Typography sx={{ mt: 1, color: colors.textFaint, fontSize: '0.8rem' }}>
            Live telemetry is off for this user.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '0.6rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: colors.textMuted,
          fontWeight: 700,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, fontFamily: '"JetBrains Mono", monospace' }}>
        {value}
      </Typography>
    </Box>
  );
}
